/**
 * ZeitraumPicker.jsx
 * Zeitraum-Auswahl: Heute / Diese Woche / (Dieser Monat) / Von-Bis
 *
 * In AgentDetailView (Live-Sicht) wird Monat nicht angeboten — es gibt eine
 * eigene Year-Card im Live-Block. Fuer Analyse-Views (TicketAnalysisPage) ist
 * Monat hilfreich — daher als Prop `includeMonat` toggelbar.
 *
 * Minimum: 01.01.2026
 */

import React, { useState, useMemo } from 'react';

const BASE_PRESETS = [
  { key: 'heute', label: 'Heute' },
  { key: 'woche', label: 'Diese Woche' },
];

// Projektbeginn. Bewusst eine Kopie: das Frontend kann die Backend-Konstante
// nicht importieren. Massgeblich ist PROJEKT_START_ISO in
// backend/src/utils/dateUtils.js — dort steht auch die Begruendung. Hier wirkt
// der Wert nur als min-Attribut der Datumsfelder; das Backend kappt jede
// Anfrage ohnehin. Wer den Projektbeginn verschiebt, zieht diese Zeile nach.
const MIN_DATE = '2026-01-01';

// ─── ISO-Wochen (identische Logik wie backend/src/utils/dateUtils.js) ─────────

function isoWeekMonday(isoYear, isoWeek) {
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const dow = jan4.getUTCDay() || 7;
  const week1Mon = new Date(jan4);
  week1Mon.setUTCDate(jan4.getUTCDate() - dow + 1);
  const result = new Date(week1Mon);
  result.setUTCDate(week1Mon.getUTCDate() + (isoWeek - 1) * 7);
  return result;
}

function toISOWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dow = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dow);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

/** Alle Kalendermonate des laufenden Jahres bis zum aktuellen Monat (exkl.), neueste zuerst. */
function getMonthsThisYear() {
  const now = new Date();
  const calYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-indexed
  const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  const fmt = d => d.toISOString().slice(0, 10);

  const months = [];
  // Startet bei currentMonth - 1: der laufende Monat hat bereits den
  // dedizierten "Dieser Monat"-Button — im Dropdown waere er doppelt.
  for (let m = currentMonth - 1; m >= 0; m--) {
    const first = new Date(Date.UTC(calYear, m, 1));
    const last  = new Date(Date.UTC(calYear, m + 1, 0));
    months.push({
      key:   `${calYear}-${String(m + 1).padStart(2, '0')}`,
      label: `${MONTH_NAMES[m]} ${calYear}`,
      from:  fmt(first),
      to:    fmt(last),
    });
  }
  return months;
}

/** Alle ISO-Wochen des laufenden Jahres bis zur aktuellen Woche, neueste zuerst. */
function getWeeksThisYear() {
  const now = new Date();
  const calYear = now.getFullYear();
  const { week: currentWeek } = toISOWeek(now);
  const fmt = d => d.toISOString().slice(0, 10);
  const fmtDe = d => `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.`;

  const weeks = [];
  // Startet bei currentWeek - 1: die laufende Woche hat bereits den dedizierten
  // "Diese Woche"-Button — im Dropdown würde sie denselben Zeitraum doppelt anbieten.
  for (let w = currentWeek - 1; w >= 1; w--) {
    const monday = isoWeekMonday(calYear, w);
    if (monday.getUTCFullYear() !== calYear) continue;
    const friday = new Date(monday);
    friday.setUTCDate(monday.getUTCDate() + 4);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    weeks.push({
      key:        `${calYear}-KW${String(w).padStart(2, '0')}`,
      label:      `KW ${w}`,
      // Anzeige nur Mo-Fr (Sa/So sind keine Arbeitstage) — der tatsaechliche
      // Datenbereich (from/to) bleibt Mo-So, identisch zum 'woche'-Preset.
      labelFull:  `KW ${w} (${fmtDe(monday)}–${fmtDe(friday)})`,
      from:       fmt(monday),
      to:         fmt(sunday),
    });
  }
  return weeks;
}

export default function ZeitraumPicker({ value, onChange, includeMonat = false }) {
  const isMonday = new Date().getDay() === 1;
  const isFirstOfMonth = new Date().getDate() === 1;
  const PRESETS = BASE_PRESETS.map(p => p.key === 'woche' && isMonday ? { ...p, disabled: true } : p);
  const isCustom = typeof value === 'object' && value !== null;
  const [customVon, setCustomVon] = useState(
    isCustom ? value.from : MIN_DATE
  );
  const [customBis, setCustomBis] = useState(
    isCustom ? value.to : new Date().toISOString().split('T')[0]
  );
  const [showCustom, setShowCustom] = useState(isCustom);

  const weeks  = useMemo(() => getWeeksThisYear(), []);
  const months = useMemo(() => includeMonat ? getMonthsThisYear() : [], [includeMonat]);
  const matchedWeek  = isCustom ? weeks.find(w => w.from === value.from && w.to === value.to) : null;
  const matchedMonth = isCustom ? months.find(m => m.from === value.from && m.to === value.to) : null;

  const handlePreset = (key) => {
    setShowCustom(false);
    onChange(key);
  };

  const handleWeekSelect = (key) => {
    const wk = weeks.find(w => w.key === key);
    if (!wk) return;
    setShowCustom(false);
    onChange({ from: wk.from, to: wk.to });
  };

  const handleMonthSelect = (key) => {
    const mo = months.find(m => m.key === key);
    if (!mo) return;
    setShowCustom(false);
    onChange({ from: mo.from, to: mo.to });
  };

  const handleCustomApply = () => {
    if (customVon && customBis && customVon <= customBis) {
      onChange({ from: customVon, to: customBis });
    }
  };

  const activeKey   = typeof value === 'string' ? value : null;
  const wochePreset = PRESETS.find(p => p.key === 'woche');
  const weekActive  = activeKey === 'woche' || !!matchedWeek;
  const monthActive = activeKey === 'monat' || !!matchedMonth;

  return (
    <div className="zeitraum-picker">
      <div className="zeitraum-presets">
        {PRESETS.filter(p => p.key !== 'woche').map(p => (
          <button
            key={p.key}
            className={`zeitraum-btn ${activeKey === p.key ? 'active' : ''} ${p.disabled ? 'disabled' : ''}`}
            onClick={() => !p.disabled && handlePreset(p.key)}
            disabled={p.disabled}
            title={p.disabled ? 'Am Montag identisch mit "Heute"' : undefined}
          >
            {p.label}
          </button>
        ))}

        {/* "Diese Woche" + Kalenderwoche-Auswahl als ein verschmolzener Pill-Button.
            Button-Text bleibt immer "Diese Woche" — das ist der Weg zurück zur
            aktuellen Woche, unabhaengig davon welche KW im Dropdown gewaehlt ist. */}
        <div className={`zeitraum-week-group ${weekActive ? 'active' : ''}`}>
          <button
            className="zeitraum-week-btn"
            onClick={() => !wochePreset.disabled && handlePreset('woche')}
            disabled={wochePreset.disabled}
            title={wochePreset.disabled ? 'Am Montag identisch mit "Heute"' : undefined}
          >
            Diese Woche
          </button>
          {/* Sichtbares Label zeigt nur "KW 26" (ohne Datum) — das Datum
              erscheint erst in der aufgeklappten Liste. Das eigentliche
              <select> liegt unsichtbar (opacity:0) darüber und fängt die
              Klicks ab — so bestimmt der lange Optionstext nie die Breite
              der sichtbaren Box (das war der vorherige Render-Bug). */}
          <div className="zeitraum-week-kw-wrap">
            <span className="zeitraum-week-kw-display">{matchedWeek ? matchedWeek.label : ''}</span>
            <span className="zeitraum-week-kw-arrow">▾</span>
            <select
              className="zeitraum-week-kw-select"
              value={matchedWeek ? matchedWeek.key : ''}
              onChange={e => handleWeekSelect(e.target.value)}
              title="Andere Kalenderwoche wählen"
            >
              <option value="" disabled></option>
              {weeks.map(w => (
                <option key={w.key} value={w.key}>{w.labelFull}</option>
              ))}
            </select>
          </div>
        </div>

        {/* "Dieser Monat" + Monats-Auswahl — gleicher verschmolzener Pill-Button
            wie bei "Diese Woche". Nur in Analyse-Views (includeMonat). */}
        {includeMonat && (
          <div className={`zeitraum-week-group ${monthActive ? 'active' : ''}`}>
            <button
              className="zeitraum-week-btn"
              onClick={() => !isFirstOfMonth && handlePreset('monat')}
              disabled={isFirstOfMonth}
              title={isFirstOfMonth ? 'Am 1. des Monats identisch mit "Heute"' : undefined}
            >
              Dieser Monat
            </button>
            <div className="zeitraum-week-kw-wrap">
              <span className="zeitraum-week-kw-display">{matchedMonth ? matchedMonth.label : ''}</span>
              <span className="zeitraum-week-kw-arrow">▾</span>
              <select
                className="zeitraum-week-kw-select"
                value={matchedMonth ? matchedMonth.key : ''}
                onChange={e => handleMonthSelect(e.target.value)}
                title="Anderen Monat wählen"
              >
                <option value="" disabled></option>
                {months.map(m => (
                  <option key={m.key} value={m.key}>{m.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <button
          className={`zeitraum-btn ${showCustom ? 'active' : ''}`}
          onClick={() => setShowCustom(s => !s)}
        >
          Von–Bis
        </button>
      </div>

      {showCustom && (
        <div className="zeitraum-custom">
          <label>
            Von
            <input
              type="date"
              min={MIN_DATE}
              max={customBis}
              value={customVon}
              onChange={e => setCustomVon(e.target.value)}
            />
          </label>
          <label>
            Bis
            <input
              type="date"
              min={customVon || MIN_DATE}
              max={new Date().toISOString().split('T')[0]}
              value={customBis}
              onChange={e => setCustomBis(e.target.value)}
            />
          </label>
          <button
            className="zeitraum-btn zeitraum-apply"
            onClick={handleCustomApply}
            disabled={!customVon || !customBis || customVon > customBis}
          >
            Anwenden
          </button>
        </div>
      )}
    </div>
  );
}
